import { useQuery } from "@tanstack/react-query";
import { ApiClientError } from "../services/api-client";
import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingHistoryEventDto,
  CoachingParticipantDto,
  CoachingProgramDto,
  CoachingResourceDto,
  CoachingSessionDto,
  CoachingUserDto,
  ListResponse
} from "../services/coaching-dtos";
import { getCoachingDataMode, getCoachingServices, type CoachingServices } from "../services/coaching-service-provider";
import { useWalkReadinessHold } from "../lib/walk-readiness";
import { coachingQueryKeys } from "./coaching-query-keys";

/**
 * Live product read bindings.
 *
 * In live data mode every list/detail surface renders backend rows through
 * these TanStack Query bindings. In mock/evidence mode the bindings stay
 * disabled (`isLiveBound: false`) so the deterministic contract fixtures keep
 * backing the e2e evidence suite without any network traffic.
 *
 * Backend scope denials (403) are surfaced as an explicit `denied` posture so
 * surfaces can render an honest denied/empty state instead of widening
 * permissions client-side or faking rows.
 */

type Query = Record<string, string | number | boolean | null | undefined>;

export interface LiveListFamilies {
  "companies.list": CoachingCompanyDto;
  "programs.list": CoachingProgramDto;
  "cohorts.list": CoachingCohortDto;
  "participants.list": CoachingParticipantDto;
  "sessions.list": CoachingSessionDto;
  "reports.list": CoachingArtifactDto;
  "resources.list": CoachingResourceDto;
  "history.list": CoachingHistoryEventDto;
  "users.list": CoachingUserDto;
  "users.assignableCoaches": CoachingUserDto;
}
export type LiveListFamily = keyof LiveListFamilies;

const liveListCalls: {
  [K in LiveListFamily]: (services: CoachingServices, query?: Query) => Promise<ListResponse<LiveListFamilies[K]>>;
} = {
  "companies.list": (services, query) => services.companies.list(query),
  "programs.list": (services, query) => services.programs.list(query),
  "cohorts.list": (services, query) => services.cohorts.list(query),
  "participants.list": (services, query) => services.participants.list(query),
  "sessions.list": (services, query) => services.sessions.list(query),
  "reports.list": (services, query) => services.reports.list(query),
  "resources.list": (services, query) => services.resources.list(query),
  "history.list": (services, query) => services.history.list(query),
  "users.list": (services, query) => services.users.list(query),
  "users.assignableCoaches": (services, query) => services.users.assignableCoaches(query)
};

export interface LiveRecordFamilies {
  "companies.detail": CoachingCompanyDto;
  "cohorts.detail": CoachingCohortDto;
  "sessions.detail": CoachingSessionDto;
  "resources.detail": CoachingResourceDto;
}

export type LiveRecordFamily = keyof LiveRecordFamilies;

const liveRecordCalls: {
  [K in LiveRecordFamily]: (services: CoachingServices, recordId: string) => Promise<LiveRecordFamilies[K]>;
} = {
  "companies.detail": (services, recordId) => services.companies.detail(recordId),
  "cohorts.detail": (services, recordId) => services.cohorts.detail(recordId),
  "sessions.detail": (services, recordId) => services.sessions.detail(recordId),
  "resources.detail": (services, recordId) => services.resources.detail(recordId)
};

export interface LiveBindingStatus {
  isLiveBound: boolean;
  isLoading: boolean;
  isError: boolean;
  denied: boolean;
  isEmpty: boolean;
  reference?: string;
}

export interface LiveListBinding<TItem> extends LiveBindingStatus {
  items: TItem[];
}

export interface LiveRecordBinding<TItem> extends LiveBindingStatus {
  record: TItem | null;
}

export const liveProductQueryPolicy = {
  retry: false,
  staleTime: 30_000
};

export function isLiveCoachingDataMode() {
  return getCoachingDataMode() === "live";
}

export function useLiveCoachingList<F extends LiveListFamily>(
  family: F,
  options: { query?: Query; enabled?: boolean } = {}
): LiveListBinding<LiveListFamilies[F]> {
  const enabled = isLiveCoachingDataMode() && (options.enabled ?? true);
  const query = useQuery({
    queryKey: coachingQueryKeys.list(family, options.query ?? {}),
    queryFn: () => liveListCalls[family](getCoachingServices(), options.query),
    enabled,
    ...liveProductQueryPolicy
  });
  useWalkReadinessHold(enabled && query.isLoading);

  return {
    ...liveBindingStatus(enabled, query.isLoading, query.error),
    isEmpty: enabled && query.isSuccess && query.data.items.length === 0,
    items: (enabled && query.data?.items) || []
  };
}

export function useLiveCoachingRecord<F extends LiveRecordFamily>(
  family: F,
  recordId: string | null,
  options: { enabled?: boolean } = {}
): LiveRecordBinding<LiveRecordFamilies[F]> {
  const enabled = isLiveCoachingDataMode() && Boolean(recordId) && (options.enabled ?? true);
  const query = useQuery({
    queryKey: coachingQueryKeys.detail(family, recordId ?? "missing"),
    queryFn: () => liveRecordCalls[family](getCoachingServices(), recordId ?? ""),
    enabled,
    ...liveProductQueryPolicy
  });
  useWalkReadinessHold(enabled && query.isLoading);

  return {
    ...liveBindingStatus(enabled, query.isLoading, query.error),
    isEmpty: false,
    record: (enabled && query.data) || null
  };
}

export function useLiveCohortParticipants(cohortId: string | null): LiveListBinding<CoachingParticipantDto> {
  return useLiveCohortChildren("participants.listForCohort", cohortId, (services, id) =>
    services.participants.listForCohort(id)
  );
}

export function useLiveCohortSessions(cohortId: string | null): LiveListBinding<CoachingSessionDto> {
  return useLiveCohortChildren("sessions.listForCohort", cohortId, (services, id) => services.sessions.listForCohort(id));
}

function useLiveCohortChildren<TItem>(
  family: "participants.listForCohort" | "sessions.listForCohort",
  cohortId: string | null,
  load: (services: CoachingServices, cohortId: string) => Promise<ListResponse<TItem>>
): LiveListBinding<TItem> {
  const enabled = isLiveCoachingDataMode() && Boolean(cohortId);
  const query = useQuery({
    queryKey: coachingQueryKeys.list(family, { cohort_id: cohortId ?? "missing" }),
    queryFn: () => load(getCoachingServices(), cohortId ?? ""),
    enabled,
    ...liveProductQueryPolicy
  });
  useWalkReadinessHold(enabled && query.isLoading);

  return {
    ...liveBindingStatus(enabled, query.isLoading, query.error),
    isEmpty: enabled && query.isSuccess && query.data.items.length === 0,
    items: (enabled && query.data?.items) || []
  };
}

function liveBindingStatus(enabled: boolean, isLoading: boolean, error: unknown) {
  const denied = enabled && error instanceof ApiClientError && error.status === 403;
  return {
    isLiveBound: enabled,
    isLoading: enabled && isLoading,
    isError: enabled && Boolean(error) && !denied,
    denied,
    isEmpty: false,
    reference: error instanceof ApiClientError ? (error.reference ?? undefined) : undefined
  };
}

/**
 * Live rows are already role-scoped by the backend, so live search filtering
 * is a plain client-side match (no mock scope fixture involved).
 */
export function searchLiveRows<T extends Record<string, unknown>>(
  rows: T[],
  searchTerm: string | undefined,
  searchableFields: Array<keyof T>
): { rows: T[]; emptyReason: "no-authorized-match" | null } {
  const normalizedTerm = String(searchTerm ?? "")
    .trim()
    .toLocaleLowerCase();
  if (!normalizedTerm) return { rows, emptyReason: null };

  const filtered = rows.filter((row) =>
    searchableFields.some((field) =>
      String(row[field] ?? "")
        .toLocaleLowerCase()
        .includes(normalizedTerm)
    )
  );
  return { rows: filtered, emptyReason: filtered.length === 0 ? "no-authorized-match" : null };
}

/**
 * The live Coaching API expects integer ids in mutation bodies while the
 * frontend keeps ids as strings everywhere. Canonical WRITE keys are snake_case
 * (`company_id`, `program_id`, `coach_user_id`; session reassign takes
 * `new_coach_id`) — the read DTOs expose `coach_id`, which the API does NOT
 * accept on writes (proven by the 2026-07-05 deterministic user-path walk).
 */
export function asLiveApiId(value: string): number | string {
  return /^[0-9]+$/.test(value) ? Number(value) : value;
}
